import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { AnalyticsAttributes } from '@atlassian/projects-goals-common/analytics';
import { createPlatformSiteAri } from '@atlassian/projects-goals-common/create-platform-site-ari';
import { getSiteAri } from '@atlassian/projects-goals-common/get-site-ari';
import { getSiteId } from '@atlassian/projects-goals-common/get-site-id';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, { type ProjectPanelQuery } from './__generated__/ProjectPanelQuery.graphql';
import type { ProductName, TabType } from './types';

type EntryPointParams = { ari: string };
const fetchPolicy: PreloadFetchPolicy = 'network-only';

type AtlasProjectEntryPointComponent = EntryPointComponent<
	{
		queryReference: ProjectPanelQuery;
	},
	Record<string, never>,
	{
		parentProduct: ProductName;
		cloudId: string;
		analyticsAttributes?: AnalyticsAttributes;
		organizationId?: string;
		url?: string;
		defaultTab?: TabType;
		mediaProvider?: Promise<MediaProvider>;
	},
	Record<string, never>
>;

const atlasProjectEntryPoint: EntryPoint<AtlasProjectEntryPointComponent, EntryPointParams> =
	createEntryPoint({
		root: JSResourceForInteraction<AtlasProjectEntryPointComponent>(() => import(/* webpackChunkName: "@atlaskit-internal_@atlassian/entry-point-fixtures/ProjectPanel" */ "./ProjectPanel"), { moduleId: "db5db9ea", moduleName: "ProjectPanel", modulePath: "../platform/packages/townsquare/project-panel/src/ProjectPanel.tsx", priority: "interaction", moduleId: 'atlas-project-entrypoint' }),
		getPreloadProps: ({ ari }: EntryPointParams) => {
			traceUFOPress('townsquare-project-panel-opened');

			// check for M0 or greater
			const isProjectNativeIntegrationEnabled =
				FeatureGates.getExperimentValue(
					'atlassian_projects_-_native_integration',
					'releaseVersion',
					-1,
				) >= 0;

			const siteId = getSiteId('project', ari);
			const queryContext = siteId ? createPlatformSiteAri(siteId) : null;

			const capabilitiesContainerId = siteId ? getSiteAri(siteId) : '';

			return {
				queries: {
					queryReference: {
						parameters,
						variables: {
							ari,
							isProjectNativeIntegrationEnabled,
							queryContext,
							capabilitiesContainerId,
							cloudId: siteId,
						},
						options: {
							fetchPolicy,
						},
					},
				},
			};
		},
	});

export default atlasProjectEntryPoint;
